import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { ContractorDefectCard } from "./ContractorDefectCard";

const GROUP_DEFS = [
  {
    id: "incomplete",
    title: "Incomplete",
    dotClass: "bg-amber-500",
    match: (d) => d.status !== "completed" && !d.closed_out,
  },
  {
    id: "complete",
    title: "Complete",
    dotClass: "bg-green-500",
    match: (d) => d.status === "completed" && !d.closed_out,
  },
  {
    id: "closed-out",
    title: "Closed Out",
    dotClass: "bg-slate-800",
    match: (d) => !!d.closed_out,
  },
];

const Group = ({ def, defects, open, onToggle, onSetStatus, updatingId }) => (
  <div
    className="border border-slate-200 rounded-lg overflow-hidden bg-white"
    data-testid={`contractor-group-${def.id}`}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
      data-testid={`contractor-group-toggle-${def.id}`}
    >
      <span className="flex items-center gap-2 font-semibold text-slate-800">
        <span className={`w-2.5 h-2.5 rounded-full ${def.dotClass}`} />
        {def.title} ({defects.length})
      </span>
      <ChevronDown
        className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
    {open && (
      <div className="p-3 space-y-3 border-t border-slate-100 bg-slate-50/50">
        {defects.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">
            No {def.title.toLowerCase()} defects.
          </p>
        ) : (
          defects.map((d) => (
            <ContractorDefectCard
              key={d.id}
              defect={d}
              onSetStatus={onSetStatus}
              updating={updatingId === d.id}
            />
          ))
        )}
      </div>
    )}
  </div>
);

/**
 * Renders contractor defects split into collapsible "Incomplete", "Complete"
 * and "Closed Out" groups (all collapsed by default). `onExpandedChange` is
 * called with the array of currently-expanded category ids (used by the
 * internal page to print only the expanded categories).
 */
export const ContractorDefectGroups = ({
  defects,
  onSetStatus,
  updatingId,
  testid,
  onExpandedChange,
}) => {
  const [open, setOpen] = useState({
    incomplete: false,
    complete: false,
    "closed-out": false,
  });

  useEffect(() => {
    if (onExpandedChange) {
      onExpandedChange(GROUP_DEFS.filter((g) => open[g.id]).map((g) => g.id));
    }
    // eslint-disable-next-line
  }, [open]);

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="space-y-3" data-testid={testid}>
      {GROUP_DEFS.map((def) => (
        <Group
          key={def.id}
          def={def}
          defects={defects.filter(def.match)}
          open={open[def.id]}
          onToggle={() => toggle(def.id)}
          onSetStatus={onSetStatus}
          updatingId={updatingId}
        />
      ))}
    </div>
  );
};
